import { getAppConfiguration } from "@holisto-web/config";
import { parseLiteral } from "@holisto-web/shared";
import { headerMenuLanguagesKeys } from "./translations.js";

export const AllLanguages: SameRecord<ILanguage> = {
  EN: "EN",
  ES: "ES",
  DE: "DE",
  FR: "FR",
  IT: "IT",
  PT: "PT",
  PL: "PL",
  NL: "NL",
  FI: "FI",
  NO: "NO",
  DA: "DA",
  SV: "SV",
};

export const getSupportedLanguages = (): SameRecord<ILanguage> => {
  const { supportedLanguages } = getAppConfiguration();

  return supportedLanguages.reduce(
    (results, nextLanguage) => ({ ...results, [nextLanguage]: nextLanguage }),
    {} as SameRecord<ILanguage>,
  );
};

// Partners may pass languageCode as "en", "en-gb", "en_gb", "nb-no", or "no-no" (case insensitive).
export function getLanguagePart(localeCode: string): string {
  return localeCode.split(/[-_]/)[0].toUpperCase();
}

export function getLocalePart(localeCode: string): string {
  return (localeCode.split(/[-_]/)[1] || "").toUpperCase();
}

const LANGUAGE_PART_ALIASES: Record<string, ILanguage> = {
  NB: "NO",
};

const normalizeLanguagePart = (languagePart: string, supportedLanguages: SameRecord<ILanguage>): string => {
  const alias = LANGUAGE_PART_ALIASES[languagePart];

  if (alias && supportedLanguages[alias]) {
    return alias;
  }

  return languagePart;
};

export const getSupportedLanguageCode = (languageCode: string | null | undefined): ILanguage | undefined => {
  if (!languageCode) {
    return undefined;
  }

  const supportedLanguages = getSupportedLanguages();
  const languagePart = normalizeLanguagePart(getLanguagePart(languageCode), supportedLanguages);

  return parseLiteral(languagePart, supportedLanguages);
};

export const parseLanguageCodeOverride = (
  languageCodeParam: string | null | undefined,
): { languageCode?: ILanguage; countryCode?: string } => {
  if (!languageCodeParam) {
    return {};
  }

  const languageCode = getSupportedLanguageCode(languageCodeParam);

  if (!languageCode) {
    return {};
  }

  const localePart = getLocalePart(languageCodeParam);

  return {
    languageCode,
    ...(localePart ? { countryCode: localePart } : {}),
  };
};

export interface ILanguageInfo {
  availableLocales: string[];
  defaultLocale: string;
  translationsKey: string;
}

export const languages: Record<ILanguage, ILanguageInfo> = {
  EN: {
    defaultLocale: "en-US",
    availableLocales: ["en-US"],
    translationsKey: headerMenuLanguagesKeys.english,
  },
  ES: {
    defaultLocale: "es-ES",
    availableLocales: ["es-ES"],
    translationsKey: headerMenuLanguagesKeys.spanish,
  },
  DE: {
    defaultLocale: "de-DE",
    availableLocales: ["de-DE"],
    translationsKey: headerMenuLanguagesKeys.german,
  },
  IT: {
    defaultLocale: "it-IT",
    availableLocales: ["it-IT"],
    translationsKey: headerMenuLanguagesKeys.italian,
  },
  FR: {
    defaultLocale: "fr-FR",
    availableLocales: ["fr-FR"],
    translationsKey: headerMenuLanguagesKeys.french,
  },
  PT: {
    defaultLocale: "pt-BR",
    availableLocales: ["pt-BR"],
    translationsKey: headerMenuLanguagesKeys.portuguese,
  },
  PL: {
    defaultLocale: "pl-PL",
    availableLocales: ["pl-PL"],
    translationsKey: headerMenuLanguagesKeys.polish,
  },
  NL: {
    defaultLocale: "nl-NL",
    availableLocales: ["nl-NL"],
    translationsKey: headerMenuLanguagesKeys.dutch,
  },
  FI: {
    defaultLocale: "fi-FI",
    availableLocales: ["fi-FI"],
    translationsKey: headerMenuLanguagesKeys.finnish,
  },
  NO: {
    defaultLocale: "nb-NO",
    availableLocales: ["nb-NO"],
    translationsKey: headerMenuLanguagesKeys.norwegian,
  },
  DA: {
    defaultLocale: "da-DK",
    availableLocales: ["da-DK"],
    translationsKey: headerMenuLanguagesKeys.danish,
  },
  SV: {
    defaultLocale: "sv-SE",
    availableLocales: ["sv-SE"],
    translationsKey: headerMenuLanguagesKeys.swedish,
  },
};

export const selectLocaleCode = (languageCode: ILanguage, countryCode: string) => {
  const localePart = getLocalePart(`${languageCode}-${countryCode}`);

  return (
    languages[languageCode].availableLocales.find((locale) => {
      const normalizedLocale = locale.toUpperCase();

      return (
        normalizedLocale === `${languageCode}-${localePart}` ||
        (localePart !== "" && getLocalePart(locale) === localePart)
      );
    }) || languages[languageCode].defaultLocale
  );
};
